import React from 'react';
import { defineMessages, type IntlShape } from 'react-intl';
import type {
	ExtensionManifest,
	PreloadableExtensionModuleNode,
} from '@atlaskit/editor-common/extensions/extension-manifest';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import AppIcon from '@atlaskit/icon/core/app';

import {
	EXTENSION_TYPE,
	EXTENSION_KEY,
} from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import type { InvalidExtensionParameters } from '@confluence/fabric-extension-handlers/entry-points/extensionHandlerTypes.ts';
import {
	getMigrationExtHandlerForEditor,
	getMigrationExtHandlerForRenderer,
	showUnmodifiableMacroFlag,
} from '@confluence/fabric-extension-handlers/entry-points/MigrationExtension.ts';
import { expValEquals } from '@confluence/feature-experiments';

import { ClassicEditorContextProviderLazyLoader } from '../../../components/ClassicEditorContextProvider/ClassicEditorContextProviderLazyLoader.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

const i18n = defineMessages({
	widthLabel: {
		id: 'confluence-fabric-editor.migration-extension.manifest.width.label',
		defaultMessage: 'Width',
		description: "Label for the width field in the macro's config panel",
	},
	widthDescription: {
		id: 'confluence-fabric-editor.migration-extension.manifest.width.description',
		defaultMessage: 'Width of image',
		description: "Description of the width field in the macro's config panel",
	},
	heightLabel: {
		id: 'confluence-fabric-editor.migration-extension.manifest.height.label',
		defaultMessage: 'Height',
		description: "Label for the height field in the macro's config panel",
	},
	heightDescription: {
		id: 'confluence-fabric-editor.migration-extension.manifest.height.description',
		defaultMessage: 'Height of image',
		description: "Description of the width field in the macro's config panel",
	},
	configPlaceholder: {
		id: 'confluence-fabric-editor.migration-extension.manifest.config.placeholder',
		defaultMessage: 'auto',
		description: 'Place holder text when width or height config input is empty',
	},
});

type GetExtensionHandler = typeof getMigrationExtHandlerForEditor;

export function getMigrationExtensionManifest(
	contentId: string,
	isEditor: boolean,
	intl: IntlShape,
	flags?: FlagsStateContainer,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): ExtensionManifest<InvalidExtensionParameters>[] {
	if (expValEquals('enable_edit_of_inline_image_extensions', 'cohort', 'test')) {
		return getMigrationExtensionManifestV2(contentId, isEditor, intl, flags, createAnalyticsEvent);
	}

	const type = 'inlineExtension';
	const getExtensionHandler = isEditor
		? getMigrationExtHandlerForEditor
		: getMigrationExtHandlerForRenderer;

	return [
		EXTENSION_KEY.INVALID,
		EXTENSION_KEY.INLINE_MEDIA_IMAGE,
		EXTENSION_KEY.INLINE_EXTERNAL_IMAGE,
	].map((key) => {
		const node: PreloadableExtensionModuleNode<InvalidExtensionParameters> = {
			type,
			async preloadRender() {
				// Intentionally left empty. But the function needs to be defined
			},
			renderSync() {
				return ({ node }) =>
					getExtensionHandler({
						contentId,
						contextProviders: {
							ClassicEditorContextProvider: ClassicEditorContextProviderLazyLoader,
						},
					})(node, {});
			},
			async render() {
				return this.renderSync();
			},
			update: isEditor
				? (parameters) =>
						showUnmodifiableMacroFlag({ type, attrs: { parameters } }, flags, createAnalyticsEvent)
				: undefined,
		};

		return {
			title: 'Migration Extension',
			key,
			type: EXTENSION_TYPE.MIGRATION,
			icons: {
				'48': async () => () => <AppIcon label="Migration Extension" />,
			},
			modules: {
				nodes: {
					default: node,
				},
			},
		};
	});
}

const getMigrationExtensionManifestV2 = (
	contentId: string,
	isEditor: boolean,
	intl: IntlShape,
	flags?: FlagsStateContainer,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): ExtensionManifest<InvalidExtensionParameters>[] => {
	const getExtensionHandler = isEditor
		? getMigrationExtHandlerForEditor
		: getMigrationExtHandlerForRenderer;

	const invalidExtensionManifest = createInvalidMigrationExtensionManifest(
		contentId,
		getExtensionHandler,
		isEditor,
		flags,
		createAnalyticsEvent,
	);

	const inlineImagesExtensionManifests = [
		EXTENSION_KEY.INLINE_MEDIA_IMAGE,
		EXTENSION_KEY.INLINE_EXTERNAL_IMAGE,
	].map((key) =>
		createInlineImagesMigrationExtensionManifest(
			key,
			contentId,
			getExtensionHandler,
			isEditor,
			intl,
		),
	);

	return [invalidExtensionManifest, ...inlineImagesExtensionManifests];
};

const createInlineImagesMigrationExtensionManifest = (
	key: string,
	contentId: string,
	getExtensionHandler: GetExtensionHandler,
	isEditor: boolean,
	intl: IntlShape,
): ExtensionManifest<InvalidExtensionParameters> => {
	const node: PreloadableExtensionModuleNode<InvalidExtensionParameters> = {
		type: 'inlineExtension',
		async preloadRender() {
			// Intentionally left empty. But the function needs to be defined
		},
		renderSync() {
			return ({ node }) =>
				getExtensionHandler({
					contentId,
					contextProviders: {
						ClassicEditorContextProvider: ClassicEditorContextProviderLazyLoader,
					},
				})(node, {});
		},
		async render() {
			return this.renderSync();
		},
		async getFieldsDefinition() {
			return [
				{
					name: 'width',
					label: intl.formatMessage(i18n.widthLabel),
					isRequired: false,
					description: intl.formatMessage(i18n.widthDescription),
					placeholder: intl.formatMessage(i18n.configPlaceholder),
					type: 'number',
				},
				{
					name: 'height',
					label: intl.formatMessage(i18n.heightLabel),
					isRequired: false,
					description: intl.formatMessage(i18n.heightDescription),
					placeholder: intl.formatMessage(i18n.configPlaceholder),
					type: 'number',
				},
			];
		},
		update: isEditor
			? async (_, actions) =>
					actions!.editInContextPanel(
						(parameters) => {
							return {
								...parameters,
								width: Number(parameters.width) || undefined,
								height: Number(parameters.height) || undefined,
							};
						},
						(parameters) => {
							return {
								...parameters,
								width: transformImageSize(parameters.width, 16),
								height: transformImageSize(parameters.height, 8),
							};
						},
					)
			: undefined,
	};

	return createMigrationExtenionManifest(key, node);
};

const createInvalidMigrationExtensionManifest = (
	contentId: string,
	getExtensionHandler: GetExtensionHandler,
	isEditor: boolean,
	flags?: FlagsStateContainer,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
): ExtensionManifest<InvalidExtensionParameters> => {
	const node: PreloadableExtensionModuleNode<InvalidExtensionParameters> = {
		type: 'inlineExtension',
		async preloadRender() {
			// Intentionally left empty. But the function needs to be defined
		},
		renderSync() {
			return ({ node }) =>
				getExtensionHandler({
					contentId,
					contextProviders: {
						ClassicEditorContextProvider: ClassicEditorContextProviderLazyLoader,
					},
				})(node, {});
		},
		async render() {
			return this.renderSync();
		},
		update: isEditor
			? (parameters) =>
					showUnmodifiableMacroFlag(
						{ type: 'inlineExtension', attrs: { parameters } },
						flags,
						createAnalyticsEvent,
					)
			: undefined,
	};

	return createMigrationExtenionManifest(EXTENSION_KEY.INVALID, node);
};

const transformImageSize = (size: number | undefined, minSize: number): string => {
	if (size === undefined) {
		return '';
	}

	if (size <= minSize) {
		return String(minSize);
	}

	return String(size);
};

const createMigrationExtenionManifest = (
	key: string,
	node: PreloadableExtensionModuleNode<InvalidExtensionParameters>,
): ExtensionManifest<InvalidExtensionParameters> => {
	return {
		title: 'Migration Extension',
		key,
		type: EXTENSION_TYPE.MIGRATION,
		icons: {
			'48': async () => () => <AppIcon label="Migration Extension" />,
		},
		modules: {
			nodes: {
				default: node,
			},
		},
	};
};
